import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CalendarDays, Pencil, Plus, Trash2 } from 'lucide-react'
import { api, messageErreur } from '../../lib/api'
import type { Donnee, Venue } from '../../lib/types'
import { Carte, Champ, EtatVide, Message, Modale, Pastille } from '../base'
import * as f from '../../lib/format'
import { TYPES_VENUE } from '@doc/shared'

export default function Venues({
  patientId,
  venues,
  diagnostics,
  ouvrirFormulaire,
  surFermeture,
}: {
  patientId: string
  venues: Venue[]
  diagnostics: Donnee[]
  ouvrirFormulaire: boolean
  surFermeture: () => void
}) {
  const [edition, setEdition] = useState<Venue | null>(null)
  const [ajout, setAjout] = useState(false)

  const ouvert = ajout || ouvrirFormulaire

  return (
    <Carte
      titre="Consultations et venues"
      icone={<CalendarDays className="h-[17px] w-[17px] text-bleu-500" />}
      compte={venues.length}
      actions={
        <button className="bouton-discret px-2" onClick={() => setAjout(true)} title="Nouvelle venue">
          <Plus className="h-4 w-4" />
        </button>
      }
    >
      {venues.length === 0 ? (
        <EtatVide
          texte="Aucune venue enregistrée."
          action={
            <button className="bouton-doux mt-1" onClick={() => setAjout(true)}>
              <Plus className="h-4 w-4" />
              Créer une venue
            </button>
          }
        />
      ) : (
        <ul className="divide-y divide-trait pb-2">
          {venues.map((venue) => (
            <li key={venue.id} className="group px-5 py-3">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[13px] font-medium text-bleu-700">
                      {f.date(venue.date)}
                    </span>
                    <Pastille ton="bleu">{TYPES_VENUE[venue.type]}</Pastille>
                  </div>
                  {venue.motif && <p className="mt-1 text-[14px]">{venue.motif}</p>}
                  {venue.diagnostics.length > 0 && (
                    <p className="mt-0.5 text-[12.5px] text-ardoise">
                      Diagnostics : {venue.diagnostics.map((d) => d.libelle).join(', ')}
                    </p>
                  )}
                  {venue.notes && (
                    <p className="mt-1 text-[13px] whitespace-pre-wrap text-ardoise">
                      {venue.notes}
                    </p>
                  )}
                  {venue.conduiteATenir && (
                    <p className="mt-1 rounded-lg bg-bleu-50 px-3 py-2 text-[13px] text-bleu-700">
                      Conduite à tenir : {venue.conduiteATenir}
                    </p>
                  )}
                  {venue.documents.length > 0 && (
                    <p className="mt-1 text-[12px] text-brume">
                      {venue.documents.length} document{venue.documents.length > 1 ? 's' : ''}{' '}
                      rattaché{venue.documents.length > 1 ? 's' : ''}
                    </p>
                  )}
                </div>

                <button
                  className="bouton-discret px-2 opacity-0 group-hover:opacity-100"
                  onClick={() => setEdition(venue)}
                  title="Corriger la venue"
                >
                  <Pencil className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {(ouvert || edition) && (
        <FormulaireVenue
          patientId={patientId}
          venue={edition ?? undefined}
          diagnostics={diagnostics}
          fermer={() => {
            setAjout(false)
            setEdition(null)
            surFermeture()
          }}
        />
      )}
    </Carte>
  )
}

function FormulaireVenue({
  patientId,
  venue,
  diagnostics,
  fermer,
}: {
  patientId: string
  venue?: Venue
  diagnostics: Donnee[]
  fermer: () => void
}) {
  const client = useQueryClient()
  const [valeurs, setValeurs] = useState({
    date: venue ? f.pourChampDate(venue.date) : f.pourChampDate(new Date()),
    type: venue?.type ?? 'CONSULTATION',
    motif: venue?.motif ?? '',
    notes: venue?.notes ?? '',
    conduiteATenir: venue?.conduiteATenir ?? '',
    diagnosticIds: venue?.diagnostics.map((d) => d.id) ?? [],
  })
  const [erreur, setErreur] = useState<string | null>(null)

  const enregistrer = useMutation({
    mutationFn: () =>
      venue
        ? api(`/api/venues/${venue.id}`, { methode: 'PATCH', corps: valeurs })
        : api(`/api/patients/${patientId}/venues`, { methode: 'POST', corps: valeurs }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['dossier', patientId] })
      client.invalidateQueries({ queryKey: ['accueil'] })
      fermer()
    },
    onError: (e) => setErreur(messageErreur(e)),
  })

  const supprimer = useMutation({
    mutationFn: () => api(`/api/venues/${venue!.id}`, { methode: 'DELETE' }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['dossier', patientId] })
      fermer()
    },
    onError: (e) => setErreur(messageErreur(e)),
  })

  return (
    <Modale
      titre={venue ? 'Corriger la venue' : 'Nouvelle venue'}
      fermer={fermer}
      piedDePage={
        <>
          {venue && (
            <button
              className="bouton-discret mr-auto text-critique"
              onClick={() => {
                if (window.confirm('Supprimer cette venue ?')) supprimer.mutate()
              }}
            >
              <Trash2 className="h-4 w-4" />
              Supprimer
            </button>
          )}
          <button className="bouton-discret" onClick={fermer}>
            Annuler
          </button>
          <button
            className="bouton-primaire"
            onClick={() => enregistrer.mutate()}
            disabled={enregistrer.isPending}
          >
            Enregistrer
          </button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Champ etiquette="Date *">
          <input
            className="champ"
            type="date"
            value={valeurs.date}
            onChange={(e) => setValeurs((v) => ({ ...v, date: e.target.value }))}
          />
        </Champ>
        <Champ etiquette="Type">
          <select
            className="champ"
            value={valeurs.type}
            onChange={(e) => setValeurs((v) => ({ ...v, type: e.target.value as Venue['type'] }))}
          >
            {Object.entries(TYPES_VENUE).map(([cle, libelle]) => (
              <option key={cle} value={cle}>
                {libelle}
              </option>
            ))}
          </select>
        </Champ>
        <Champ etiquette="Motif" className="sm:col-span-2">
          <input
            className="champ"
            value={valeurs.motif}
            onChange={(e) => setValeurs((v) => ({ ...v, motif: e.target.value }))}
          />
        </Champ>
        <Champ etiquette="Notes" className="sm:col-span-2">
          <textarea
            className="champ min-h-[90px]"
            value={valeurs.notes}
            onChange={(e) => setValeurs((v) => ({ ...v, notes: e.target.value }))}
          />
        </Champ>
        <Champ etiquette="Conduite à tenir" className="sm:col-span-2">
          <textarea
            className="champ min-h-[64px]"
            value={valeurs.conduiteATenir}
            onChange={(e) => setValeurs((v) => ({ ...v, conduiteATenir: e.target.value }))}
          />
        </Champ>

        {diagnostics.length > 0 && (
          <Champ
            etiquette="Diagnostics concernés"
            aide="Parmi les diagnostics validés du dossier."
            className="sm:col-span-2"
          >
            <div className="flex flex-col gap-1.5 pt-1">
              {diagnostics.map((d) => (
                <label key={d.id} className="flex items-center gap-2 text-[13.5px]">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[var(--color-bleu-600)]"
                    checked={valeurs.diagnosticIds.includes(d.id)}
                    onChange={(e) =>
                      setValeurs((v) => ({
                        ...v,
                        diagnosticIds: e.target.checked
                          ? [...v.diagnosticIds, d.id]
                          : v.diagnosticIds.filter((id) => id !== d.id),
                      }))
                    }
                  />
                  {d.libelle}
                </label>
              ))}
            </div>
          </Champ>
        )}

        {erreur && (
          <div className="sm:col-span-2">
            <Message ton="critique">{erreur}</Message>
          </div>
        )}
      </div>
    </Modale>
  )
}
